import { useState } from "react";
import { Banknote, CheckCircle2, Clock3, Coins, FileText, HandCoins, Info, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useLanguage } from "@/lib/i18n";
import { openReceiptPdfWindow } from "@/lib/receipt-pdf";

interface SalaryAdvanceTabProps {
  idToken: string | null;
}

export function SalaryAdvanceTab({ idToken }: SalaryAdvanceTabProps) {
  const { t, language } = useLanguage();
  const tx = (th: string, my: string) => (language === "my" ? my : th);
  const [amount, setAmount] = useState<string>("");
  const [reason, setReason] = useState<string>("");

  const advanceQuery = trpc.salaryAdvance.dashboard.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken), staleTime: 10000 }
  );

  const requestMutation = trpc.salaryAdvance.request.useMutation({
    onSuccess: () => {
      toast.success(
        tx(
          "ยื่นคำขอเบิกเงินล่วงหน้าเรียบร้อยแล้ว รอผู้บริหาร/HR อนุมัติ",
          "လစာကြိုတင်ထုတ်ယူရန် တင်ပြပြီးပါပြီ။ အတည်ပြုချက်ကို စောင့်ပါ"
        )
      );
      setAmount("");
      setReason("");
      void advanceQuery.refetch();
    },
    onError: (err) => {
      toast.error(
        err.message || tx("เกิดข้อผิดพลาดในการยื่นคำขอ", "တောင်းဆိုမှုတင်ရာတွင် အမှားဖြစ်ပွားသည်")
      );
    },
  });

  const data = advanceQuery.data;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!idToken) {
      toast.error(tx("กรุณาเข้าสู่ระบบก่อนยื่นคำขอ", "တောင်းဆိုမှုမတင်မီ အကောင့်ဝင်ပါ"));
      return;
    }
    const numAmount = parseInt(amount, 10);
    if (isNaN(numAmount) || numAmount <= 0) {
      toast.error(
        tx("กรุณาระบุจำนวนเงินที่ต้องการขอเบิกให้ถูกต้อง", "ထုတ်ယူလိုသည့် ငွေပမာဏကို မှန်ကန်စွာ ထည့်ပါ")
      );
      return;
    }
    if (data && numAmount > data.maxAllowed) {
      toast.error(
        tx(
          `ไม่สามารถเบิกเกินวงเงินสิทธิ์ที่คำนวณได้ (ทำงาน ${data.workedDays} วัน คำนวณสิทธิ์ได้ ${data.eligibleDays} วัน = ฿${data.earnedWage.toLocaleString()}, ขอเบิกได้สูงสุด ฿${data.maxAllowed.toLocaleString()})`,
          `ရရှိပြီးလုပ်အားခထက် ကျော်၍ မထုတ်ယူနိုင်ပါ (${data.workedDays} ရက် = ฿${data.earnedWage.toLocaleString()}, အများဆုံး ฿${data.maxAllowed.toLocaleString()})`
        )
      );
      return;
    }

    requestMutation.mutate({
      idToken,
      amount: numAmount,
      reason: reason.trim() || undefined,
    });
  };

  return (
    <div className="employee-module salary-advance-module space-y-4">
      {/* Overview Banner */}
      <Card className="glass-panel rounded-3xl overflow-hidden shadow-lg border border-[#e5ede7]">
        <div className="h-1.5 bg-gradient-to-r from-[#1b6b44] via-[#248a58] to-[#86efac]" />
        <CardHeader className="p-5 sm:p-7 border-b border-[#e7ede8]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ebf6ee] text-[#1b6b44]">
                <HandCoins size={26} />
              </div>
              <div>
                <span className="text-[11px] font-black uppercase tracking-wider text-[#1b6b44]">
                  SALARY ADVANCE SYSTEM
                </span>
                <CardTitle className="text-xl sm:text-2xl font-black text-slate-900">
                  {t.advanceHeading}
                </CardTitle>
                <CardDescription className="text-xs font-semibold text-slate-500 mt-0.5">
                  {t.advanceSubheading}
                </CardDescription>
              </div>
            </div>

            {data?.period && (
              <Badge className="bg-[#ebf6ee] text-[#1b6b44] hover:bg-[#ebf6ee] px-3.5 py-1.5 text-xs font-black rounded-full border border-[#cbe4d2]">
                {data.period.label}
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-5 sm:p-7 space-y-6">
          {advanceQuery.isLoading ? (
            <div className="text-center py-8">
              <Coins className="animate-spin text-[#1b6b44] mx-auto mb-2" size={30} />
              <p className="text-sm font-bold text-slate-600">
                {tx("กำลังคำนวณสิทธิการขอเบิกเงินล่วงหน้า...", "ကြိုတင်ထုတ်ယူခွင့်ကို တွက်ချက်နေသည်...")}
              </p>
            </div>
          ) : data ? (
            <>
              {/* Calculation Rule Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-2xl bg-[#f8faf8] p-3.5 border border-[#e7ede8]">
                  <p className="text-xs font-bold text-slate-500">{tx("อัตราค่าแรงต่อวัน", "နေ့စားနှုန်း")}</p>
                  <p className="mt-1 text-2xl font-black font-numeric text-slate-900">
                    ฿{data.dailyRate.toLocaleString()}
                  </p>
                  <p className="text-[11px] font-semibold text-slate-400 mt-0.5">
                    {tx("คำนวณตามข้อมูลพนักงาน", "ဝန်ထမ်းဒေတာအရ တွက်ချက်သည်")}
                  </p>
                </div>

                <div className="rounded-2xl bg-[#f8faf8] p-3.5 border border-[#e7ede8]">
                  <p className="text-xs font-bold text-slate-500">{t.advanceWorkedDays}</p>
                  <p className="mt-1 text-2xl font-black font-numeric text-[#1b6b44]">
                    {data.workedDays} {tx("วัน", "ရက်")}
                  </p>
                  <p className="text-[11px] font-semibold text-emerald-700 mt-0.5">
                    {tx(`สิทธิ์เบิกสะสม ${data.eligibleDays} วัน`, `ထုတ်ယူခွင့်ရက် ${data.eligibleDays} ရက်`)} (฿{data.earnedWage.toLocaleString()})
                  </p>
                </div>

                <div className="rounded-2xl bg-[#f8faf8] p-3.5 border border-[#e7ede8]">
                  <p className="text-xs font-bold text-slate-500">{tx("ยอดที่เบิกไปแล้วในงวดนี้", "ယခုကာလ ထုတ်ယူပြီးငွေ")}</p>
                  <p className="mt-1 text-2xl font-black font-numeric text-amber-700">
                    ฿{data.usedAdvanceAmount.toLocaleString()}
                  </p>
                  <p className="text-[11px] font-semibold text-slate-400 mt-0.5">
                    {tx("รวมรออนุมัติและอนุมัติแล้ว", "ဆိုင်းငံ့နှင့် အတည်ပြုပြီး စုစုပေါင်း")}
                  </p>
                </div>

                <div className="rounded-2xl bg-[#ebf6ee] p-3.5 border border-[#cbe4d2]">
                  <p className="text-xs font-black text-[#1b6b44]">{t.advanceMaxLimit}</p>
                  <p className="mt-1 text-2xl font-black font-numeric text-[#1b6b44]">
                    ฿{data.maxAllowed.toLocaleString()}
                  </p>
                  <p className="text-[11px] font-bold text-emerald-700 mt-0.5">
                    {data.maxAllowed > 0
                      ? tx("สามารถยื่นขอเบิกได้", "တောင်းဆိုနိုင်သည်")
                      : tx("ยังไม่มีวงเงินเบิก", "ထုတ်ယူနိုင်သည့်ငွေ မရှိသေးပါ")}
                  </p>
                </div>
              </div>

              {/* Policy Explanation Note with Cooldown Status */}
              <div className="rounded-2xl bg-amber-50/80 border border-amber-200 p-3.5 space-y-2 text-xs text-amber-900">
                <div className="flex items-start gap-2.5">
                  <Info size={18} className="text-amber-700 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-black">
                      {tx("เงื่อนไขการขอเบิกเงินล่วงหน้า (ตามกฎบริษัท):", "ကြိုတင်ထုတ်ယူခြင်း စည်းမျဉ်းများ:")}
                    </span>
                    <p className="mt-0.5 leading-relaxed font-semibold text-amber-800">
                      • ขอเบิกได้ 1 ครั้งทุกๆ {data.cooldownDays ?? 2} วัน (เว้นระยะห่าง {data.cooldownDays ?? 2} วัน)<br />
                      • ทำงานสะสมครบ {data.workDaysPerCut ?? 3} วัน ตัดสิทธิ์ให้เบิก {data.allowedCuts ?? 2} วัน (เพื่อสำรองเงินงวด)
                    </p>
                  </div>
                </div>

                {data.canRequestNow === false && (
                  <div className="rounded-xl bg-rose-50 border border-rose-200 p-2.5 flex items-center gap-2 text-rose-800 font-bold">
                    <Clock3 size={16} className="text-rose-600 shrink-0" />
                    <span>
                      {tx(
                        `คุณเพิ่งยื่นขอเบิกไปเมื่อเร็วๆ นี้ สามารถยื่นขอเบิกได้อีกครั้งในอีก ${data.cooldownRemainingHours} ชั่วโมง (ตามกฎ 2 วันเบิกได้ 1 ครั้ง)`,
                        `မကြာသေးမီက တောင်းဆိုထားပါသည်။ နာရီ ${data.cooldownRemainingHours} အကြာတွင် ပြန်လည်တောင်းဆိုနိုင်ပါသည်`
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Request Form */}
              <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-black uppercase text-slate-700 mb-1.5">
                      {t.advanceAmountLabel} <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      min={100}
                      max={data.maxAllowed}
                      step={50}
                      placeholder={t.advanceAmountPlaceholder}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      disabled={data.maxAllowed <= 0 || data.canRequestNow === false || requestMutation.isPending}
                      className="w-full rounded-2xl border border-[#d9e3dc] bg-white px-4 py-3 text-base font-bold text-slate-900 shadow-xs focus:border-[#1b6b44] focus:ring-2 focus:ring-[#1b6b44]/20 outline-none disabled:bg-slate-100 disabled:cursor-not-allowed"
                    />
                    <p className="text-[11px] font-semibold text-slate-400 mt-1">
                      {t.advanceMaxLimit} ฿{data.maxAllowed.toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-black uppercase text-slate-700 mb-1.5">
                      {t.advanceReasonLabel}
                    </label>
                    <input
                      type="text"
                      placeholder={t.advanceReasonPlaceholder}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={data.maxAllowed <= 0 || data.canRequestNow === false || requestMutation.isPending}
                      className="w-full rounded-2xl border border-[#d9e3dc] bg-white px-4 py-3 text-sm font-bold text-slate-900 shadow-xs focus:border-[#1b6b44] focus:ring-2 focus:ring-[#1b6b44]/20 outline-none disabled:bg-slate-100 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={data.maxAllowed <= 0 || data.canRequestNow === false || !amount || parseInt(amount, 10) > data.maxAllowed || requestMutation.isPending}
                  className="btn-primary-teal h-13 px-8 rounded-full text-sm font-black shadow-lg shadow-[#1b6b44]/20 w-full sm:w-auto"
                >
                  <HandCoins size={18} className="mr-2" />
                  {requestMutation.isPending ? "..." : t.advanceSubmitBtn}
                </Button>
              </form>

              {/* History Table */}
              <div className="pt-4 border-t border-[#e7ede8]">
                <h3 className="text-sm font-black text-slate-900 mb-3 flex items-center gap-2">
                  <Banknote size={16} className="text-[#1b6b44]" />
                  {t.advanceHistoryTitle}
                </h3>

                {data.advances.length === 0 ? (
                  <p className="text-xs font-semibold text-slate-400 py-3 text-center">
                    {tx("ยังไม่มีประวัติการขอเบิกเงินล่วงหน้า", "ကြိုတင်ထုတ်ယူမှု မှတ်တမ်းမရှိသေးပါ")}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {data.advances.map((item: any) => (
                      <div
                        key={item.id}
                        className="rounded-2xl border border-[#e7ede8] bg-white p-3.5 flex flex-wrap items-center justify-between gap-3 shadow-xs"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-base font-black font-numeric text-slate-900">
                              ฿{Number(item.amount).toLocaleString()}
                            </span>
                            <span className="text-xs font-semibold text-slate-400">
                              · {item.periodLabel}
                            </span>
                          </div>
                          <p className="text-xs font-semibold text-slate-500 mt-0.5">
                            {tx("เหตุผล", "အကြောင်းပြချက်")}: {item.reason || tx("ขอเบิกเงินล่วงหน้า", "လစာကြိုတင်ထုတ်ယူခြင်း")}
                          </p>
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            {tx("ยื่นคำขอเมื่อ", "တောင်းဆိုသည့်ရက်")} {new Date(item.createdAt).toLocaleDateString(language === "my" ? "my-MM" : "th-TH")} ({tx("ทำงานแล้ว", "အလုပ်လုပ်ပြီး")} {item.workedDaysAtRequest} {tx("วัน", "ရက်")})
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          {item.status === "approved" && (
                            <>
                              <Badge className="bg-[#ebf6ee] text-[#1b6b44] hover:bg-[#ebf6ee] border-[#cbe4d2] rounded-full px-3 py-1 font-bold text-xs flex items-center gap-1">
                                <CheckCircle2 size={13} />
                                {tx("อนุมัติแล้ว", "အတည်ပြုပြီး")}
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openReceiptPdfWindow({
                                  receiptNo: `ADV-${item.periodLabel.replace(/[^0-9]/g, "").slice(0, 6) || "2026"}-${String(item.id).padStart(4, "0")}`,
                                  employeeName: item.displayName || data.profile.displayName,
                                  amount: Number(item.amount),
                                  periodLabel: item.periodLabel,
                                  workedDays: item.workedDaysAtRequest,
                                  approvedAt: item.decidedAt || item.createdAt,
                                  approverName: item.approverName || "ฝ่ายบริหาร (HR)",
                                  reason: item.reason,
                                })}
                                className="h-8 rounded-full border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 text-xs font-bold"
                              >
                                <FileText size={13} className="mr-1" />
                                {tx("ใบเสร็จ PDF", "ပြေစာ PDF")}
                              </Button>
                            </>
                          )}
                          {item.status === "rejected" && (
                            <Badge className="bg-rose-50 text-rose-700 hover:bg-rose-50 border-rose-200 rounded-full px-3 py-1 font-bold text-xs flex items-center gap-1">
                              <XCircle size={13} />
                              {tx("ไม่อนุมัติ", "ပယ်ချပြီး")}
                            </Badge>
                          )}
                          {item.status === "pending" && (
                            <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 border-amber-200 rounded-full px-3 py-1 font-bold text-xs flex items-center gap-1">
                              <Clock3 size={13} />
                              {tx("รอการอนุมัติ", "အတည်ပြုချက်စောင့်နေသည်")}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-xs font-semibold text-rose-500">
              {tx("ไม่สามารถโหลดข้อมูลสิทธิได้", "အခွင့်အရေးဒေတာကို ဖွင့်၍မရပါ")}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
